import classNames from 'clsx';
import React, { type ReactElement, memo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { enabledPublicDownloadLimits, tooltipTrackDownload } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import DownloadCounter from 'reactApp/components/DownloadCounter/DownloadCounter';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getFirstPublicWithDownloadLimits, getPublicLimits } from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { DataListControl } from 'reactApp/ui/DataListControl/DataListControl';
import { DatalistPic } from 'reactApp/ui/Mobile/Datalist/DatalistPic/DatalistPic';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { TrackDownloadCountPromo } from 'reactApp/ui/SharingTooltips/TrackDownloadCountPromo';
import { Icon20ChainOutline, Icon20MoreDotsVertical } from 'reactApp/ui/touchIcons';
import { noop } from 'reactApp/utils/helpers';

import styles from './DatalistRowItem.css';
import type { IProps } from './DatalistRowItem.types';

const defaultOptionIconElement = <Icon20MoreDotsVertical className={styles.icon} width={20} height={20} fill="currentColor" />;

export const DatalistRowItem = memo(
    ({
        id,
        isFolder,
        kind,
        ext,
        name,
        size,
        isVirus,
        isSelecting,
        isSelected,
        thumbUrl,
        onClick,
        onDoubleClick,
        onOptions,
        onShare,
        showOptionIcon = true,
        optionIconElement = defaultOptionIconElement,
        weblink,
        author,
        onQuotaCleanerSelect,
        moreBtnRef,
    }: IProps): ReactElement => {
        const storage = useSelector(getCurrentStorage);
        const tooltipRef = useRef<HTMLDivElement>(null);
        const { isPublic, isQuotaCleaner } = getStorage(storage);
        const [showTooltipCounter, setShowTooltipCounter] = useState(false);

        // tempexp_17847-start
        const TOOLTIP_ID = 'tooltipTrackDownload';
        const isTooltipShown = Boolean(storeHelper.getValue(TOOLTIP_ID));
        const isPaidUser = useSelector(UserSelectors.isPaidUser);
        const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, (weblink as string) || ''));
        const downloadTotal = downloadLimits && downloadLimits.total;
        const downloadLeft = downloadLimits && downloadLimits.left;
        const downloadCount = downloadTotal - downloadLeft;
        const showDownloadCounter =
            enabledPublicDownloadLimits &&
            (storage === EStorageType.home || storage === EStorageType.sharedLinks) &&
            !isPaidUser &&
            downloadCount > 0;

        const firstPublicWithDownloads = useSelector((state: RootState) => getFirstPublicWithDownloadLimits(state));
        const weblinkForPromo = firstPublicWithDownloads && Object.keys(firstPublicWithDownloads)[0];
        const showTrackDownloadCountPromo = tooltipTrackDownload && showDownloadCounter;
        const showFirstTrackDownloadCountPromo = showTrackDownloadCountPromo && (weblink as string) === weblinkForPromo;

        const handleCounterClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowTooltipCounter(true);
        };

        const handleCounterPromoHide = () => {
            setShowTooltipCounter(false);
        };
        // tempexp_17847-end

        return (
            <div
                className={classNames(styles.root, { [styles.root_selecting]: isSelecting })}
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                data-qa-id={id}
                data-qa-type={isFolder ? 'folder' : 'file'}
                data-qa-name={ext ? `${name}.${ext}` : name}
            >
                {isSelecting && (
                    <div className={styles.check} onClick={onQuotaCleanerSelect}>
                        <DataListControl type="check" active={isSelected} size={24} viewType={EViewMode.list} />
                    </div>
                )}
                <div className={styles.pic}>
                    <DatalistPic
                        isFolder={isFolder}
                        ext={ext || ''}
                        kind={kind}
                        thumbUrl={thumbUrl}
                        isVirus={isVirus}
                        viewMode={EViewMode.list}
                        author={author}
                    />
                </div>
                <div className={styles.info}>
                    <div className={styles.name}>
                        <NameComponent name={name} extension={ext} truncate />
                    </div>
                    <div className={styles.size}>{size}</div>
                </div>

                <div className={styles.buttons}>
                    {!isPublic && Boolean(weblink) && (
                        <div className={styles.button_publish} onClick={isSelecting ? noop : onShare}>
                            <Icon20ChainOutline className={styles.icon} width={20} height={20} />
                        </div>
                    )}
                    {showDownloadCounter && (
                        <div ref={tooltipRef}>
                            <DownloadCounter
                                downloads={downloadCount}
                                downloadLimit={downloadTotal}
                                isListViewMobile
                                onClick={handleCounterClick}
                            />
                        </div>
                    )}
                    {showFirstTrackDownloadCountPromo && !isTooltipShown && (
                        <TrackDownloadCountPromo targetRef={tooltipRef} downloads={downloadCount} downloadLimit={downloadTotal} isMobile />
                    )}
                    {showTrackDownloadCountPromo && showTooltipCounter && (
                        <TrackDownloadCountPromo
                            targetRef={tooltipRef}
                            downloads={downloadCount}
                            downloadLimit={downloadTotal}
                            onHide={handleCounterPromoHide}
                            isMobile
                        />
                    )}
                    {showOptionIcon && optionIconElement && !isQuotaCleaner && (
                        <div ref={moreBtnRef} className={styles.button_more} onClick={isSelecting ? noop : onOptions}>
                            {optionIconElement}
                        </div>
                    )}
                </div>
            </div>
        );
    }
);

DatalistRowItem.displayName = 'DatalistRowItem';
